import * as React from "react";
import { MultiSelect, IOption } from "../../atoms/forms/MultiSelect";
import { IApplicationState } from "../../../store";
import * as Disabilities from "../../../store/disabilities";
import { FetchState } from "../../../entities/FetchState";
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { IData as DisabilityData } from '../../../store/disabilities/disabilitiesList';
import { IStoreMultiSelectControls } from "./ICommonInputsControls";

// Disability interface from the store
type Disability = DisabilityData;


interface IDisability extends IOption {
    disability: Disability;
}

const mapDisabilityToValue = (disability: Disability): IDisability => {
    return {
        value: disability.id,
        label: disability.name,
        disability: disability
    }
}

// Props interface
interface IProps {
    selectedDisabilityIds?: string[];
}

// Actual Component
export type AllProps = IProps & IStoreMultiSelectControls<Disability>;
export const DisabilityMultiSelect = (props: AllProps) => {
    const { t: tDatatypes } = useTranslation('datatypes');
    const { t: tCommon } = useTranslation('common');
    
    // Get Redux state and dispatch
    const disabilitiesState = useSelector((state: IApplicationState) => state.disabilities.disabilitiesList);
    const dispatch = useDispatch();

    // Internal state for selected disabilities - initialize with preselected disabilities if available
    const [selectedDisabilities, setSelectedDisabilities] = React.useState<IDisability[]>(() => {
        // Try to initialize with preselected disabilities if data is already available
        if (disabilitiesState.DisabilitiesList.data && props.selectedDisabilityIds) {
            const disabilities = disabilitiesState.DisabilitiesList.data
                .filter(d => props.selectedDisabilityIds!.includes(d.id))
                .map(mapDisabilityToValue);
            return disabilities;
        }
        // If using value from common interface
        else if (props.value) {
            return props.value.map(mapDisabilityToValue);
        }
        return [];
    });

    // Fetch disabilities on initial render if not loaded or if the data array is empty
    React.useEffect(() => {
        const shouldFetch = 
            disabilitiesState.DisabilitiesList.state === FetchState.NotFetched || 
            (disabilitiesState.DisabilitiesList.state === FetchState.Success && 
             (!disabilitiesState.DisabilitiesList.data || disabilitiesState.DisabilitiesList.data.length === 0));
             
        if (shouldFetch) {
            dispatch(Disabilities.ActionCreators.disabilitiesList.FetchData());
        }
    }, [dispatch, disabilitiesState.DisabilitiesList.state, disabilitiesState.DisabilitiesList.data]);

    // Update selected disabilities when data is loaded or selectedDisabilityIds prop changes
    React.useEffect(() => {
        if (disabilitiesState.DisabilitiesList.data) {
            if (props.selectedDisabilityIds) {
                const disabilities = disabilitiesState.DisabilitiesList.data
                    .filter(d => props.selectedDisabilityIds!.includes(d.id))
                    .map(mapDisabilityToValue);
                setSelectedDisabilities(disabilities);
            } else if (props.value) {
                // Handle value from common interface
                setSelectedDisabilities(props.value.map(mapDisabilityToValue));
            } else {
                setSelectedDisabilities([]);
            }
        }
    }, [disabilitiesState.DisabilitiesList.data, props.selectedDisabilityIds, props.value, disabilitiesState.DisabilitiesList.state]);

    // Map disabilities to select options
    const disabilities = React.useMemo(() => {
        return (disabilitiesState.DisabilitiesList.data || [])
            .map(mapDisabilityToValue)
            .sort((a, b) => {
                if (a.label && b.label) {
                    return a.label.localeCompare(b.label);
                }
                // Fallback: compare by id if label is undefined
                return String(a.value).localeCompare(String(b.value));
            });
    }, [disabilitiesState.DisabilitiesList.data, disabilitiesState.DisabilitiesList.state]);

    // Force component re-render when disabilities list changes state from loading to success
    const [forceUpdateKey, setForceUpdateKey] = React.useState(0);
    React.useEffect(() => {
        if (disabilitiesState.DisabilitiesList.state === FetchState.Success && disabilitiesState.DisabilitiesList.data && disabilitiesState.DisabilitiesList.data.length > 0) {
            setForceUpdateKey(prev => prev + 1);
        }
    }, [disabilitiesState.DisabilitiesList.state, disabilitiesState.DisabilitiesList.data]);

    const updateDisabilities = (disabilities: IDisability[] | null) => {
        const disabilityArray = disabilities || [];
        setSelectedDisabilities(disabilityArray);
        
        // Call onChange callback from common interface
        if (props.onChange) {
            const disabilityData = disabilityArray.map(d => d.disability);
            props.onChange(disabilityData.length > 0 ? disabilityData : null, !props.required || disabilityArray.length > 0);
        }
    }

    const isDisabled = props.disabled || disabilitiesState.DisabilitiesList.state !== FetchState.Success;
    const isLoading = disabilitiesState.DisabilitiesList.state === FetchState.Busy || disabilitiesState.DisabilitiesList.state === FetchState.NotFetched || props.loading;
    const error = props.error || (disabilitiesState.DisabilitiesList.state === FetchState.Error ? [true, tCommon('errorLoadingData')] : undefined);

    return (<MultiSelect 
        key={forceUpdateKey}
        label={(props.label || tDatatypes('disabilityLabel') || "Disabilities") + (disabilitiesState.DisabilitiesList.state !== FetchState.Success ? tCommon('loading') : "")}
        placeholder={props.placeholder || (tDatatypes('disabilityPlaceholder') || "Select disabilities...")}
        options={disabilities}
        value={selectedDisabilities}
        disabled={isDisabled}
        onChange={updateDisabilities}
        loading={isLoading}
        error={error}
        required={props.required}
        prevalidate={props.prevalidate}
        isUnknownValuesEnabled={props.isUnknownValuesEnabled}
    />)
}

